import { useEffect, useRef, useState } from 'react';
import './SearchControls.css';

// `relevance` only means something when there is a query to be relevant to, so
// it is offered conditionally. The values match the API's `sort` parameter.
const SORT_OPTIONS = [
    { value: 'relevance', label: 'Relevance', short: 'Relevance', requiresQuery: true },
    { value: 'popular', label: 'Most popular', short: 'Popular' },
    { value: 'price_asc', label: 'Price: low to high', short: 'Lowest price' },
    { value: 'price_desc', label: 'Price: high to low', short: 'Highest price' },
    { value: 'newest', label: 'Newest first', short: 'Newest' },
];

const FilterIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 7H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M6 12H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M10 17H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const SortIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 7H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M3 17H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="8" cy="7" r="2" fill="currentColor" />
        <circle cx="16" cy="17" r="2" fill="currentColor" />
    </svg>
);

const CheckIcon = () => (
    <svg className="option-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Only digits, so a stray letter cannot reach the API as `min_price=abc`.
const toAmount = (raw) => raw.replace(/[^\d]/g, '');

const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

// A step that keeps the slider to a few hundred stops whatever the catalogue
// costs, so the thumb lands on round figures instead of Rs 2,999.4137.
const STEP_SIZES = [1, 5, 10, 50, 100, 500, 1000, 5000, 10000];
const stepFor = (span) => STEP_SIZES.find((step) => span / step <= 400) ?? 10000;

const SearchControls = ({ facets, value, onChange, hasQuery }) => {
    const [openPanel, setOpenPanel] = useState(null);
    const [draft, setDraft] = useState({ category: '', minPrice: '', maxPrice: '' });
    const rootRef = useRef(null);

    // Dismiss the open panel the way a popover is expected to behave.
    useEffect(() => {
        if (!openPanel) return undefined;
        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpenPanel(null);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setOpenPanel(null);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [openPanel]);

    const categories = facets?.categories || [];
    const priceRange = facets?.priceRange || {};
    const sortOptions = SORT_OPTIONS.filter((option) => hasQuery || !option.requiresQuery);
    // The API picks relevance or popular when no sort is asked for; mirror that
    // so the panel always shows what the results are actually ordered by.
    const activeSort = value.sort || (hasQuery ? 'relevance' : 'popular');
    const activeSortLabel = sortOptions.find((option) => option.value === activeSort)?.short;
    const filterCount = (value.category ? 1 : 0) + (value.minPrice || value.maxPrice ? 1 : 0);

    // Slider bounds come from the facets, so the track always spans exactly
    // what the current search can cost.
    const step = stepFor((priceRange.max ?? 0) - (priceRange.min ?? 0));
    // Widened out to the step grid so the thumbs land on round figures. The
    // slack past each end just reads as "no limit this end".
    const bound = {
        min: Math.floor((priceRange.min ?? 0) / step) * step,
        max: Math.ceil((priceRange.max ?? 0) / step) * step,
    };
    const hasRange = bound.max > bound.min;
    // An unset end sits at its bound; a typed out-of-range figure pins the
    // thumb rather than dragging the track out of shape.
    const low = draft.minPrice === '' ? bound.min : clamp(Number(draft.minPrice), bound.min, bound.max);
    const high = draft.maxPrice === '' ? bound.max : clamp(Number(draft.maxPrice), bound.min, bound.max);
    const pct = (v) => ((v - bound.min) / (bound.max - bound.min)) * 100;
    // Whichever thumb is on the crowded side takes the higher stacking order,
    // so a pair sitting on top of each other never traps the one underneath.
    const minOnTop = low > (bound.min + bound.max) / 2;

    // Landing on a bound means "no limit this end", which keeps a full-width
    // range from showing up as an applied filter.
    const dragLow = (raw) => {
        const next = Math.min(Number(raw), high);
        setDraft((d) => ({ ...d, minPrice: next <= bound.min ? '' : String(next) }));
    };
    const dragHigh = (raw) => {
        const next = Math.max(Number(raw), low);
        setDraft((d) => ({ ...d, maxPrice: next >= bound.max ? '' : String(next) }));
    };

    const toggle = (panel) => {
        if (panel === 'filter' && openPanel !== 'filter') {
            // Seed the draft from what is applied, so closing without applying
            // discards the edits rather than half-committing them.
            setDraft({
                category: value.category,
                minPrice: value.minPrice,
                maxPrice: value.maxPrice,
            });
        }
        setOpenPanel((current) => (current === panel ? null : panel));
    };

    const applyDraft = () => {
        onChange({ ...value, ...draft });
        setOpenPanel(null);
    };

    const clearAll = () => {
        const cleared = { category: '', minPrice: '', maxPrice: '' };
        setDraft(cleared);
        onChange({ ...value, ...cleared });
    };

    const chooseSort = (sort) => {
        onChange({ ...value, sort });
        setOpenPanel(null);
    };

    return (
        <div className="search-controls" ref={rootRef}>
            <div className="search-control">
                <button
                    type="button"
                    className={`search-action-btn${filterCount ? ' is-active' : ''}`}
                    onClick={() => toggle('filter')}
                    aria-expanded={openPanel === 'filter'}
                    aria-haspopup="dialog"
                >
                    <FilterIcon />
                    <span className="btn-text">Filter</span>
                    {filterCount > 0 && <span className="btn-badge">{filterCount}</span>}
                </button>

                {openPanel === 'filter' && (
                    <div className="search-panel" role="dialog" aria-label="Filter products">
                        <div className="panel-section">
                            <p className="panel-heading">Category</p>
                            <div className="panel-options">
                                <button
                                    type="button"
                                    className={`panel-option${draft.category ? '' : ' is-selected'}`}
                                    onClick={() => setDraft((d) => ({ ...d, category: '' }))}
                                >
                                    <span>All categories</span>
                                    {!draft.category && <CheckIcon />}
                                </button>
                                {categories.map((category) => (
                                    <button
                                        key={category.id}
                                        type="button"
                                        className={`panel-option${draft.category === category.slug ? ' is-selected' : ''}`}
                                        onClick={() => setDraft((d) => ({ ...d, category: category.slug }))}
                                    >
                                        <span>
                                            {category.name}
                                            <span className="option-count">{category.count}</span>
                                        </span>
                                        {draft.category === category.slug && <CheckIcon />}
                                    </button>
                                ))}
                                {categories.length === 0 && (
                                    <p className="panel-empty">No categories to filter by.</p>
                                )}
                            </div>
                        </div>

                        <div className="panel-section">
                            <p className="panel-heading">Price</p>
                            {hasRange && (
                                <div className="price-slider">
                                    <div className="price-track">
                                        <div
                                            className="price-fill"
                                            style={{ left: `${pct(low)}%`, right: `${100 - pct(high)}%` }}
                                        />
                                    </div>
                                    <input
                                        type="range"
                                        className="price-range"
                                        style={{ zIndex: minOnTop ? 4 : 3 }}
                                        min={bound.min}
                                        max={bound.max}
                                        step={step}
                                        value={low}
                                        onChange={(e) => dragLow(e.target.value)}
                                        aria-label="Minimum price"
                                    />
                                    <input
                                        type="range"
                                        className="price-range"
                                        style={{ zIndex: minOnTop ? 3 : 4 }}
                                        min={bound.min}
                                        max={bound.max}
                                        step={step}
                                        value={high}
                                        onChange={(e) => dragHigh(e.target.value)}
                                        aria-label="Maximum price"
                                    />
                                </div>
                            )}
                            <div className="price-inputs">
                                <label className="price-field">
                                    <span className="price-label">Min</span>
                                    <span className="price-control">
                                        <span className="price-prefix" aria-hidden="true">Rs</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={draft.minPrice}
                                            placeholder={priceRange.min != null ? String(priceRange.min) : '0'}
                                            onChange={(e) => setDraft((d) => ({ ...d, minPrice: toAmount(e.target.value) }))}
                                        />
                                    </span>
                                </label>
                                <span className="price-separator" aria-hidden="true" />
                                <label className="price-field">
                                    <span className="price-label">Max</span>
                                    <span className="price-control">
                                        <span className="price-prefix" aria-hidden="true">Rs</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={draft.maxPrice}
                                            placeholder={priceRange.max != null ? String(priceRange.max) : 'Any'}
                                            onChange={(e) => setDraft((d) => ({ ...d, maxPrice: toAmount(e.target.value) }))}
                                        />
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="panel-footer">
                            <button type="button" className="panel-link" onClick={clearAll} disabled={!filterCount}>
                                Clear all
                            </button>
                            <button type="button" className="panel-apply" onClick={applyDraft}>
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="search-control">
                <button
                    type="button"
                    className={`search-action-btn${value.sort ? ' is-active' : ''}`}
                    onClick={() => toggle('sort')}
                    aria-expanded={openPanel === 'sort'}
                    aria-haspopup="dialog"
                >
                    <SortIcon />
                    <span className="btn-text">Sort</span>
                    {value.sort && <span className="btn-value">{activeSortLabel}</span>}
                </button>

                {openPanel === 'sort' && (
                    <div className="search-panel search-panel-sort" role="dialog" aria-label="Sort products">
                        <div className="panel-options">
                            {sortOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`panel-option${activeSort === option.value ? ' is-selected' : ''}`}
                                    onClick={() => chooseSort(option.value)}
                                >
                                    <span>{option.label}</span>
                                    {activeSort === option.value && <CheckIcon />}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchControls;
